import config from './config'
import type { EnabledFilters } from 'components/Search/type'
import type { IntlShape } from 'react-intl'

// queryNullChildren is a placeholder for no child
export const queryNullChildren = 'none'

// queryChildrenDelimiter is delineates the boundary between children age lists
export const queryChildrenDelimiter = ';'

// SEARCH_STORAGE_KEYS is the browser local storage keys under which
// recent searches are saved. The keys are versioned by appending an
// incrementing integer to mark backward incompatible changes.
export const SEARCH_STORAGE_KEYS = {
  TRAVELLERS: 'searchTravellersHistory.2',
  LAST_DATES: 'searchLastDatesHistory.1',
  LOCATIONS: 'searchLocationsHistory.3',
  LOCATION: 'searchLocationHistory.1',
  FLEXIBLE_OPTIONS: 'searchFlexibleOptions.1',
  RECENT_SEARCHES: 'searchRecentSearches.1',
  RECENT_FILTERS: 'offerListRecentFilters',
  SEARCHED_PLACES: 'searchSearchedPlaces.1',

  /* Leaving this here commented out, so we have a record of it, because some people
     will still have data in their localstorage with this key.
     If we ever reinstate recent dates, increment the number to avoid conflicts. */
  // DATES: 'searchDatesHistory.2',
}

export const RECENT_SEARCHES_LIMIT = 5
export const SEARCHED_PLACES_LIMIT = 50
export const SEARCHED_PLACES_MAX_AGE_MONTHS = 3

export function getLocationSearchErrorMessage(intl: IntlShape): string {
  return config.searchFormSection?.escapeSynonym
    ? intl.formatMessage(
        {
          id: 'agY_9L5WV2OYADB409pU',
          defaultMessage: 'Check your spelling, try a new destination or search hotels for your next {escapeSynonym}.',
          description: 'Search error message with escape synonym when no results found',
        },
        { escapeSynonym: config.searchFormSection.escapeSynonym },
      )
    : intl.formatMessage({
        id: 'kD5CZlMsXXzze0IzBFiN',
        defaultMessage: 'Check your spelling, try a new destination or search hotels.',
        description: 'Search error message when no results found on the hotel page',
      })
}

export const HOTEL_SEARCH_TYPEAHEAD_TYPES: Array<App.SearchPlaceType> = [
  'province_state',
  'neighborhood',
  'city',
  'point_of_interest',
  'high_level_region',
  'country',
  'multi_city_vicinity',
  'continent',
  'colloquial_area',
  'airport',
  'train_station',
  'metro_station',
  'le_property',
  'le_property_unique_stays',
  'le_property_rental',
  'bedbank_property',
  'bedbank_property_unique_stays',
]

// Rollout flag for backend typeahead suggestion feature
export const TYPEAHEAD_SUGGESTION_ROLLOUT_FLAGS: Array<string> = ['typeaheadSuggestion']
export const NLS_INCLUSION_GAP_ROLLOUT_FLAGS: Array<string> = ['search_nls_inclusion_gap']

/**
 * Top-50 high-demand destination place IDs where existing MPV-based ranking
 * already works well. Low-demand fallback scoring is skipped for these.
 */
export const HIGH_DEMAND_DESTINATION_IDS = new Set([
  'le_64930b7151b6bc3e289f37d5da62ac9f', // Bali
  'le_72b32a1f754ba1c09b3695e0cb6cde7f', // Fiji
  'le_d41d8cd98f00b204e9800998ecf8427e', // Anywhere
  'le_8f85517967795eeef66c225f7883bdcb', // Thailand
  'le_2723d092b63885e0d7c260cc007e8b9d', // Maldives
  'le_15fa44474c92da27cf2125373356db4c', // Melbourne
  'le_d47bb339212ddfab0fadd99d492a5fc3', // Phuket Province
  'le_bac732346917b0a1231354ef55faf00a', // Gold Coast
  'le_bd4c9ab730f5513206b999ec0d90d1fb', // Singapore
  'le_678c61ac5b4f91ca86e112fc50e63766', // Sydney
  'le_d3d9446802a44259755d38e6d163e820', // Australia
  'le_a597e50502f5ff68e3e25b9114205d4a', // Vietnam
  'le_a55472983efa2e239b315de91576d1d3', // Nusa Dua
  'le_8337a7365ec44f3d65434a5ea4d73d17', // New South Wales
  'le_790d9f1f52e10ef90ede8efcf7f3c078', // Seminyak
  'le_7af6d6117d13124fd3e00f5cb5f04400', // Koh Samui
  'le_43baa6762fa81bb43b39c62553b2970d', // Dubai Emirate
  'le_3d3621cee6f49e93a34f3f0f654ab41a', // Queensland
  'le_9fc3d7152ba9336a670e36d0ed79bc43', // New Zealand
  'le_979d472a84804b9f647bc185a877a8b5', // Hawaii
  'le_5781a2637b476d781eb3134581b32044', // Victoria
  'le_7647966b7343c29048673252e490f736', // Japan
  'le_ff7aa1f9b4062bfee0f99a0453506f1b', // Bangkok
  'le_7c27cf9f8fe09724bb754e474610eb0e', // Perth
  'le_3416a75f4cea9109507cacd8e2f2aefc', // Cook Islands
  'le_7f7ed8ecfca9e17696ff654508efd86a', // Ubud
  'le_3fb451ca2e89b3a13095b059d8705b15', // Hamilton Island
  'le_dc2afc2240bd3cd5067a740a2cb17399', // Port Douglas
  'le_5a4773830c5a9191a6bd6c301d060ad2', // Queenstown
  'le_9061fde6d599f5ffe8612f853db8d1e3', // Sanur
  'le_58a2fc6ed39fd083f55d4182bf88826d', // Vanuatu
  'le_e14e1364b73cc4878fe29890a4c29b7e', // Noosa
  'le_b96d145f6c31e35755f4a6841574ba37', // Brisbane
  'le_66ff0e7c30438d414ff0a941b759f114', // London
  'le_f3ad1c493ba61ad63bb43d6947f42d95', // Khao Lak
  'le_1608659ea7ec15440fa87763b3f5d700', // Tokyo
  'le_a3c65c2974270fd093ee8a9bf8ae7d0b', // Malaysia
  'le_f4c2c73d7fe5eb38fd83d32a61604247', // Adelaide
  'le_5878a7ab84fb43402106c575658472fa', // Sri Lanka
  'le_b7218568a1335addb2966d47e1fe939c', // Cairns
  'le_3b1b1222c9e8e9afa9d1ee10d6e80fc0', // Sydney CBD
  'le_cb3f7087c987ceff5b306d4ff89f5db6', // Paris
  'le_32530ba0f5faff63a2f1ccb9c1bc90fc', // Melbourne CBD
  'le_82680bfec0fa08346c1b10d30a3e3d4a', // Western Australia
  'le_4311359ed4969e8401880e3c1836fbe1', // Da Nang
  'le_287643040f414c7ab7860c1b3f841149', // Rarotonga
  'le_edd47ba7fa1bbff38973841c9cd0733c', // Tasmania
  'le_735b90b4568125ed6c3f678819b6e058', // Greece
  'le_864d8b8a2daebb7269e0882b0bfb6232', // Hunter Valley
  'le_12767faf1bf8cafc7594430586c558c2', // Europe
])

export const defaultSearchVerticalRoutes = new Map<App.SearchVertical, Array<string>>([
  ['hotels', ['/:regionCode/search/hotels', '/:regionCode/hotels']],
  ['flights', ['/:regionCode/search/flights', '/:regionCode/flights']],
  ['tours', ['/:regionCode/search/tours', '/:regionCode/tours']],
  ['experiences', ['/:regionCode/search/experiences', '/:regionCode/experiences']],
  ['cruises', ['/:regionCode/search/cruises', '/:regionCode/cruises']],
  ['car-hire', ['/:regionCode/search/car-hire', '/:regionCode/car-hire']],
  ['homes-and-villas', ['/:regionCode/search/homes-and-villas']],
  ['merchandise', ['/:regionCode/search/shop', '/:regionCode/shop']],
])

export const ANYWHERE_PLACE_ID: string = 'le_d41d8cd98f00b204e9800998ecf8427e'
/** Stable `destinationName` / filter key; customer label via `useSearchLabels().anywhereLabel`. */
export const ANYWHERE_NAME: string = 'Anywhere'
export const ANYWHERE_SEARCH_ITEM: App.SearchDestination = {
  searchType: 'destination',
  value: ANYWHERE_PLACE_ID,
  format: {
    mainText: ANYWHERE_NAME,
  },
}

export const TYPE_PAGE_LIMIT = 65

export enum DATE_SEARCH_OPTION_IDS {
  FLEXIBLE = 'search-flexible-dates',
  SPECIFIC = 'search-specific-dates',
  ANYTIME = 'search-anytime-dates',
}

export type FLEXIBLE_DURATION_RANGE = DEFAULT_FLEXIBLE_DURATION_RANGE | TOUR_FLEXIBLE_DURATION_RANGE | CRUISE_FLEXIBLE_DURATION_RANGE

export enum DEFAULT_FLEXIBLE_DURATION_RANGE {
  ANY_DURATION = '1-30',
  SHORT_STAY = '1-3',
  MEDIUM_STAY = '4-7',
  LONG_STAY = '8-30',
  EMPTY = '',
}

export enum TOUR_FLEXIBLE_DURATION_RANGE {
  ANY_DURATION = '1-30',
  SHORT_STAY = '1-7',
  MEDIUM_STAY = '8-14',
  LONG_STAY = '15-30',
}

export enum CRUISE_FLEXIBLE_DURATION_RANGE {
  ANY_DURATION = '',
  SHORT_STAY = '1-4',
  MEDIUM_STAY = '5-8',
  LONG_STAY = '9-14',
  VERY_LONG_STAY = '15',
}

/** Backend `customerRatings` filter slugs keyed by GTE value. */
export const CUSTOMER_RATING_RESPONSE_KEY_BY_VALUE: Record<string, string> = {
  7: 'good',
  8: 'very_good',
  9: 'exceptional',
}

export const BALI_LAT_LNG = {
  lat: -8.3405,
  lng: 115.0920,
}

export const HOTELS_ENABLED_FILTERS: EnabledFilters = {
  inclusions: true,
  amenities: true,
  customerRating: true,
  priceFilter: config.ENABLE_HOTEL_SEARCH_PAGE_PRICE_FILTER,
  holidayTypes: true,
  propertyTypes: true,
  locations: true,
  offerTypes: config.ENABLE_VILLA_RENTALS,
  popularFilters: true,
  luxPlusFeatures: false,
  agentHubFeatures: true,
  chains: true,
}

/** Holiday-type filter slug; label via `useSearchLabels().limitedTimeLuxExclusive`. */
export const LIMITED_TIME_LUX_EXCLUSIVE = 'limited_time_lux_exclusive'
